import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Button, Textarea } from "@heroui/react";
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { HelpdeskTicketInterface } from 'shared/types/helpdesk';

type Mode = 'create' | 'link';

interface CreateInsuranceIssueModalProps {
  isOpen: boolean;
  ticket: HelpdeskTicketInterface | null;
  onClose: () => void;
  onSuccess: (issue: any, mode: Mode) => void;
}

export const CreateInsuranceIssueModal: React.FC<CreateInsuranceIssueModalProps> = ({
  isOpen,
  ticket,
  onClose,
  onSuccess,
}) => {
  const { session } = useSession();
  const [mode, setMode] = useState<Mode>('create');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [selectedExisting, setSelectedExisting] = useState<any | null>(null);
  const [description, setDescription] = useState<string>('');

  const buildingId = ticket?.building?.id;
  const unitId = ticket?.unit?.id;

  const extractRelevantText = (input: string): string => {
    if (!input) return '';
    // 1) Strip HTML comments and style/script blocks aggressively
    let cleaned = input
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');

    // 2) Convert remaining HTML to plain text via DOM
    const container = document.createElement('div');
    container.innerHTML = cleaned;
    let text = container.textContent || container.innerText || '';

    // 3) Normalize whitespace and split into lines
    text = text.replace(/\u00A0/g, ' ');
    const rawLines = text.split(/\r?\n/);

    // 4) Filter boilerplate, CSS leftovers, and headers
    const lines = rawLines
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      // Email boilerplate / disclaimers / links
      .filter((l) => !/privacy\s*policy|confidential|vertrouwelijk/i.test(l))
      .filter((l) => !/facebook\.com|linkedin\.com|instagram\.com|yally\.be/i.test(l))
      // Mail headers
      .filter((l) => !/^(van|from|verzonden|onderwerp|subject|aan|to|cc|bcc)\s*:/i.test(l))
      // CSS remnants
      .filter((l) => !/(^@|^\.|\{|\}|MsoNormal|MsoChpDefault|font-family|WordSection)/i.test(l));

    // 5) Collapse duplicates
    const result: string[] = [];
    for (const l of lines) {
      if (result.length === 0 || result[result.length - 1] !== l) {
        result.push(l);
      }
    }

    return result.join('\n');
  };

  const loadOptions = useCallback(async () => {
    if (!isOpen || !ticket) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/insurance/files`, { method: 'GET' }, session);
      if (!res.ok) return;
      const all = await res.json();
      const filtered = all.filter((iss: any) => {
        const sameBuilding = buildingId && iss.building?.id === buildingId;
        const sameUnit = unitId && iss.unit?.id === unitId;
        return !!(sameBuilding || sameUnit);
      });
      setOptions(filtered);
    } catch (e) {
      console.error(e);
      setOptions([]);
    }
  }, [isOpen, ticket, session, buildingId, unitId]);

  useEffect(() => {
    if (isOpen) {
      setMode('create');
      setSelectedExisting(null);
      setDescription(extractRelevantText(ticket?.description || ticket?.title || ''));
      loadOptions();
    }
  }, [isOpen, loadOptions]);

  const canConfirm = useMemo(() => {
    if (mode === 'link') return !!selectedExisting;
    return true;
  }, [mode, selectedExisting]);

  const handleConfirm = async () => {
    if (!ticket) return;
    try {
      setLoading(true);
      if (mode === 'create') {
        const body = {
          title: ticket.title,
          description: description?.trim() || ticket.title,
          buildingId: buildingId,
          unitId: unitId,
          helpdeskTicket: { id: ticket.id },
        };
        const res = await fetchWithAuth(
          `${API_URL}/insurance/file`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
          session
        );
        if (!res.ok) throw new Error('Failed to create insurance issue');
        const issue = await res.json();
        onSuccess(issue, 'create');
      } else if (mode === 'link' && selectedExisting) {
        const updated = await fetchWithAuth(
          `${API_URL}/helpdesk/ticket/${ticket.id}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ insuranceIssue: { id: selectedExisting.id } }) },
          session
        );
        if (!updated.ok) throw new Error('Failed to link to insurance issue');
        onSuccess(selectedExisting, 'link');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: 'max-w-2xl',
        backdrop: 'bg-black/50',
        wrapper: 'items-center justify-center',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 pb-2">
          <span className="text-xl font-semibold text-neutral-primary">Verzekeringsdossier</span>
        </ModalHeader>
        <ModalBody className="pb-6">
          <div className="space-y-4">
            <div className="text-sm">Ticket <span className="font-medium">H{ticket?.id}</span> — {ticket?.title}</div>

            <div className="flex gap-2">
              <button
                onClick={() => setMode('create')}
                className={`px-3 py-1.5 rounded-sm border text-sm ${mode==='create' ? 'bg-moss-primary text-white border-moss-primary' : 'border-neutral-primary/20 text-neutral-primary'}`}
              >
                Nieuw dossier aanmaken
              </button>
              <button
                onClick={() => setMode('link')}
                className={`px-3 py-1.5 rounded-sm border text-sm ${mode==='link' ? 'bg-moss-primary text-white border-moss-primary' : 'border-neutral-primary/20 text-neutral-primary'}`}
              >
                Koppelen aan bestaand dossier
              </button>
            </div>

            {mode === 'create' && (
              <div>
                <label className="block text-sm font-medium text-neutral-primary mb-2">
                  Beschrijving
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Beschrijf het verzekeringsdossier..."
                  variant="bordered"
                  minRows={4}
                  classNames={{
                    input: 'text-neutral-primary',
                    inputWrapper: 'border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary'
                  }}
                />
                <p className="mt-1 text-xs text-neutral-secondary-mid">Standaard overgenomen van het helpdeskticket. Je kan dit aanpassen.</p>
              </div>
            )}

            {mode === 'link' && (
              <div className="border rounded-md border-neutral-primary/20">
                <div className="max-h-64 overflow-y-auto divide-y divide-neutral-primary/10">
                  {options.length === 0 ? (
                    <div className="p-3 text-sm text-neutral-secondary-mid">Geen relevante dossiers gevonden voor dit gebouw/unit.</div>
                  ) : (
                    options.map((iss) => (
                      <label key={iss.id} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-primary/5">
                        <input
                          type="radio"
                          name="existing-issue"
                          checked={selectedExisting?.id === iss.id}
                          onChange={() => setSelectedExisting(iss)}
                          className="mt-1"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-neutral-primary">INS{iss.id} • {iss.title}</div>
                          {iss.description && (
                            <div className="text-xs text-neutral-secondary-mid line-clamp-2">{iss.description}</div>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {selectedExisting && (
                  <div className="p-3 border-t border-neutral-primary/10 bg-brick-secondary-fine/40">
                    <div className="text-xs font-semibold text-neutral-primary mb-1">Beschrijving geselecteerd dossier</div>
                    <div className="text-sm text-neutral-primary whitespace-pre-wrap">
                      {selectedExisting.description || 'Geen beschrijving beschikbaar'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onClick={onClose}>
            Annuleren
          </Button>
          <Button className="bg-moss-primary text-white" isLoading={loading} isDisabled={!canConfirm} onClick={handleConfirm}>
            {mode === 'create' ? 'Aanmaken' : 'Koppelen'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

